// ---------------------------------------------------------------------------
/**
 * The one door to the browser's stores.
 *
 * Everything this app keeps on a device goes through here: the key (prefixed
 * once, in one place), the encoding, and the fact that a browser may refuse to
 * store anything at all — which with storage switched off is not a lost
 * preference but a crash on the way in.
 *
 * A value is DEFINED where it belongs, next to the code that gives it meaning
 * (`utils/lastLocation`, `reconcile/personAliases`), NOT in a central table of
 * every key. What IS central is the register: every defined value announces
 * itself, so "what does this app keep on this device, and what of it may be
 * thrown away?" has an answer (see `storageInventory`, `clearAppStorage`).
 */
// ---------------------------------------------------------------------------

export type StorageArea = 'local' | 'session';

/**
 * Every key this app writes starts with this — the reason a wipe can find our
 * own leftovers and leave everything else alone. Keys carried over from before
 * the prefix was enforced are migrated (see `legacyKeys`).
 */
export const STORAGE_PREFIX = 'ast_';

export interface StoredValue<T> {
    /** The key as it appears in the browser, prefix included. */
    readonly key: string;
    readonly area: StorageArea;
    /** The stored value, or the fallback. Never throws. */
    read(): T;
    /** True when it landed; false when the browser refused to store it. */
    write(value: T): boolean;
    /** Remove it, and any legacy key it may still be sitting under. */
    clear(): void;
    /**
     * Called whenever this value changes — in ANOTHER tab (the browser's
     * `storage` event) or in this one (`write`/`clear` announce themselves).
     *
     * `external` says which: true means another tab did it. Anything that reacts
     * by changing THIS tab's session — signing out, say — has to look at that
     * flag, or it will react to its own doing.
     *
     * Session values only ever notify locally: `sessionStorage` belongs to one
     * tab.
     *
     * Returns the unsubscribe.
     */
    subscribe(listener: (external: boolean) => void): () => void;
}

export interface StoredValueInfo {
    key: string;
    area: StorageArea;
    /** Survives {@link clearAppStorage}: work and credentials, not caches. */
    keepOnWipe: boolean;
}

interface BaseOptions {
    /** The bare name, WITHOUT the prefix: `last_app` ⇒ `ast_last_app`. */
    key: string;
    /** `local` outlives the tab, `session` is gone when it closes. */
    area?: StorageArea;
    /**
     * Keys this value used to live under. The first read or write moves the old
     * value over and removes it, so renaming a key costs nobody their data.
     */
    legacyKeys?: readonly string[];
    /**
     * Mark what a wipe must NOT take: an offline credential, a kept token, an
     * unsynced draft. Everything else is a cache or a convenience.
     */
    keepOnWipe?: boolean;
}

const registry: StoredValueInfo[] = [];

/**
 * Every value the currently loaded modules have declared. Only that much: a
 * module nobody imported has not registered anything, which is why
 * {@link clearAppStorage} scans by prefix rather than walking this.
 */
export function storageInventory(): readonly StoredValueInfo[] {
    return registry;
}

/**
 * The store, or null when this browser has none to give.
 *
 * Resolved on every call, deliberately: a browser with site data blocked throws
 * on the PROPERTY itself, not only on the access, and the tests swap the global
 * between cases.
 */
function backend(area: StorageArea): Storage | null {
    try {
        const store =
            area === 'local'
                ? globalThis.localStorage
                : globalThis.sessionStorage;
        return store ?? null;
    } catch {
        return null;
    }
}

/**
 * Which keys have already been migrated, per STORE rather than per value: a test
 * that swaps in a fresh stand-in has to migrate again, and a browser that only
 * hands out its storage on the second ask must not be stuck with the old answer.
 */
const migratedKeys = new WeakMap<Storage, Set<string>>();

function migrate(
    store: Storage,
    key: string,
    legacyKeys: readonly string[]
): void {
    if (legacyKeys.length === 0) {
        return;
    }
    let done = migratedKeys.get(store);
    if (!done) {
        done = new Set();
        migratedKeys.set(store, done);
    }
    if (done.has(key)) {
        return;
    }
    done.add(key);
    try {
        for (const legacy of legacyKeys) {
            const raw = store.getItem(legacy);
            if (raw === null) {
                continue;
            }
            // The new key wins where both exist: something written since the
            // rename is the current value, and the leftover is only in the way.
            if (store.getItem(key) === null) {
                store.setItem(key, raw);
            }
            store.removeItem(legacy);
        }
    } catch {
        // Nothing to migrate from, or nowhere to migrate it to.
    }
}

// --- who wants to hear about a change ---------------------------------------

type ChangeListener = (external: boolean) => void;

/** `${area}:${key}` → its listeners. */
const listeners = new Map<string, Set<ChangeListener>>();

function listenerKey(area: StorageArea, key: string): string {
    return `${area}:${key}`;
}

function notify(area: StorageArea, key: string, external: boolean): void {
    for (const listener of listeners.get(listenerKey(area, key)) ?? []) {
        listener(external);
    }
}

let windowListenerInstalled = false;

/**
 * One `storage` listener for the whole app, installed with the first subscriber.
 * The browser fires it in every OTHER tab of the origin — never in the one that
 * wrote — which is exactly the half a tab cannot see for itself.
 */
function installWindowListener(): void {
    if (windowListenerInstalled || typeof window === 'undefined') {
        return;
    }
    windowListenerInstalled = true;
    window.addEventListener('storage', (event: StorageEvent) => {
        const area: StorageArea =
            event.storageArea && event.storageArea === backend('session')
                ? 'session'
                : 'local';
        if (event.key === null) {
            // Somebody emptied a whole store: everything may have changed.
            for (const set of listeners.values()) {
                for (const listener of set) {
                    listener(true);
                }
            }
            return;
        }
        notify(area, event.key, true);
    });
}

function define<T>({
    key,
    area = 'local',
    legacyKeys = [],
    keepOnWipe = false,
    fallback,
    decode,
    encode
}: BaseOptions & {
    fallback: () => T;
    /** `undefined` means "unusable" — the fallback answers instead. */
    decode: (raw: string) => T | undefined;
    encode: (value: T) => string;
}): StoredValue<T> {
    const fullKey = `${STORAGE_PREFIX}${key}`;
    registry.push({ key: fullKey, area, keepOnWipe });

    return {
        key: fullKey,
        area,

        read(): T {
            const store = backend(area);
            if (!store) {
                return fallback();
            }
            migrate(store, fullKey, legacyKeys);
            let raw: string | null;
            try {
                raw = store.getItem(fullKey);
            } catch {
                return fallback();
            }
            if (raw === null) {
                return fallback();
            }
            try {
                return decode(raw) ?? fallback();
            } catch {
                // Half-written, hand-edited, or from a version that shaped it
                // differently. A stored value is input like any other.
                return fallback();
            }
        },

        write(value: T): boolean {
            const store = backend(area);
            if (!store) {
                return false;
            }
            migrate(store, fullKey, legacyKeys);
            try {
                store.setItem(fullKey, encode(value));
                notify(area, fullKey, false);
                return true;
            } catch {
                // Full or blocked. Most of what we keep is a convenience and
                // may simply be gone; whoever stores actual WORK reads the
                // answer.
                return false;
            }
        },

        clear(): void {
            const store = backend(area);
            if (!store) {
                return;
            }
            try {
                store.removeItem(fullKey);
                for (const legacy of legacyKeys) {
                    store.removeItem(legacy);
                }
                notify(area, fullKey, false);
            } catch {
                // Nothing to remove it from.
            }
        },

        subscribe(listener: ChangeListener): () => void {
            installWindowListener();
            const id = listenerKey(area, fullKey);
            const set = listeners.get(id) ?? new Set<ChangeListener>();
            listeners.set(id, set);
            set.add(listener);
            return () => {
                set.delete(listener);
                if (set.size === 0) {
                    listeners.delete(id);
                }
            };
        }
    };
}

/**
 * A value kept as JSON — a map, a list, a record.
 *
 * `fallback` is a FUNCTION so an object default cannot be handed to two callers
 * who then mutate each other's copy. `decode` is where a shape this version no
 * longer understands gets rejected; without one, what is stored is trusted.
 */
export function defineStoredJson<T>({
    fallback,
    decode,
    ...base
}: BaseOptions & {
    fallback: () => T;
    decode?: (parsed: unknown) => T | undefined;
}): StoredValue<T> {
    return define<T>({
        ...base,
        fallback,
        decode: (raw) => {
            const parsed: unknown = JSON.parse(raw);
            return decode ? decode(parsed) : (parsed as T);
        },
        encode: (value) => JSON.stringify(value)
    });
}

/**
 * A single string, stored RAW — no quotes around it.
 *
 * That is not a detail: the token, the locale and the colour mode have been
 * lying in browsers as bare strings since before this module existed, and a
 * JSON-encoded successor would read every one of them as corrupt — in effect
 * signing everybody out.
 *
 * `allow` names the values the key may hold; anything else reads as null.
 * Writing null clears the key rather than storing the word.
 */
export function defineStoredString<T extends string = string>({
    allow,
    ...base
}: BaseOptions & {
    allow?: readonly T[];
}): StoredValue<T | null> {
    const value = define<T | null>({
        ...base,
        fallback: () => null,
        decode: (raw) =>
            !allow || allow.includes(raw as T) ? (raw as T) : undefined,
        // Never reached with null — write() below short-circuits that case.
        encode: (next) => String(next)
    });
    return {
        ...value,
        write(next: T | null): boolean {
            if (next === null) {
                value.clear();
                return true;
            }
            return value.write(next);
        }
    };
}

/**
 * Throw away what this app keeps on the device, except what must stay.
 *
 * Scans by PREFIX instead of walking the register, so a key left behind by an
 * older version of the app goes too — the register only holds the values whose
 * modules happen to be loaded. For the same reason anything that MUST survive
 * should be named in `keep` at the call site rather than trusted to have
 * registered itself; the `keepOnWipe` values that did register are kept anyway.
 *
 * Nothing calls this yet, on purpose: what a database reset, a logout or a
 * device hand-over ought to delete is a decision of its own.
 */
export function clearAppStorage(
    options: {
        keep?: readonly { key: string; area: StorageArea }[];
    } = {}
): void {
    const keep = [
        ...(options.keep ?? []),
        ...registry.filter((entry) => entry.keepOnWipe)
    ];
    for (const area of ['local', 'session'] as const) {
        const store = backend(area);
        if (!store) {
            continue;
        }
        try {
            const doomed: string[] = [];
            for (let index = 0; index < store.length; index++) {
                const key = store.key(index);
                if (!key?.startsWith(STORAGE_PREFIX)) {
                    continue;
                }
                if (
                    keep.some((kept) => kept.area === area && kept.key === key)
                ) {
                    continue;
                }
                doomed.push(key);
            }
            for (const key of doomed) {
                store.removeItem(key);
            }
        } catch {
            // A store that cannot even be listed holds nothing we put there.
        }
    }
}
